import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublicLayout } from '../../components/layout';
import toast from 'react-hot-toast';
import { getPaytabsOrder } from '../../services/paytabsService';
import { getXpayOrder } from '../../services/xpayService';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const PaymentSuccess: React.FC = () => {
  const { t } = useTranslation('subscription');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'pending' | 'error'>('loading');
  const [planId, setPlanId] = useState('');
  const [paymentScope, setPaymentScope] = useState<'subscription' | 'student_payment'>('subscription');

  useEffect(() => {
    const gateway = searchParams.get('gateway');
    const gatewayOrderId = searchParams.get('orderId');
    if (gateway === 'paytabs' || gateway === 'xpay') {
      if (!gatewayOrderId) {
        setStatus('error');
        return;
      }

      let cancelled = false;
      let attempts = 0;
      const checkOrder = async () => {
        try {
          const order =
            gateway === 'xpay'
              ? await getXpayOrder(gatewayOrderId)
              : await getPaytabsOrder(gatewayOrderId);
          if (cancelled) return;
          if (order?.status === 'COMPLETED') {
            setPlanId(order.planId || '');
            setPaymentScope(order.scope);
            setStatus('success');
            toast.success(t('paymentSuccessToast'));
            return;
          }
          if (order?.status === 'FAILED' || order?.status === 'CANCELLED') {
            setStatus('error');
            toast.error(t('paymentCaptureError'));
            return;
          }
          attempts += 1;
          if (attempts >= 30) {
            setStatus('pending');
            return;
          }
          window.setTimeout(checkOrder, 2000);
        } catch (error) {
          console.error(`${gateway} status error:`, error);
          if (!cancelled) setStatus('pending');
        }
      };

      void checkOrder();
      return () => {
        cancelled = true;
      };
    }

    setStatus('error');
  }, [searchParams, t]);

  return (
    <PublicLayout>
      <div className='min-h-screen bg-gray-50 flex items-center justify-center dark:bg-secondary-950'>
        <div className='bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center dark:bg-secondary-900'>
          {status === 'loading' && (
            <>
              <div className='w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-6' />
              <h2 className='text-xl font-bold text-gray-800 mb-2 dark:text-white'>{t('confirmingPayment')}</h2>
              <p className='text-gray-500 text-sm dark:text-gray-300'>{t('confirmingPaymentHint')}</p>
            </>
          )}

          {status === 'pending' && (
            <>
              <div className='w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-6' />
              <h2 className='text-xl font-bold text-gray-800 mb-2 dark:text-white'>
                جار تأكيد الدفع
              </h2>
              <p className='text-gray-500 text-sm mb-8 dark:text-gray-300'>
                وصلنا رجوع المتصفح، وننتظر تأكيد بوابة الدفع الخادمي قبل تحديث السجل المالي.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className='w-full bg-primary-600 text-white py-3 rounded-xl font-semibold hover:bg-primary-700 transition-colors'
              >
                {t('goDashboard')}
              </button>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircleIcon className='w-20 h-20 text-green-500 mx-auto mb-6' />
              <h2 className='text-2xl font-bold text-gray-900 mb-3 dark:text-white'>{t('paymentSucceeded')}</h2>
              <p className='text-gray-600 mb-2 dark:text-gray-300'>
                {paymentScope === 'subscription'
                  ? t('paymentPlanActivated', { plan: planId })
                  : t('studentPaymentConfirmed')}
              </p>
              <p className='text-gray-400 text-sm mb-8 dark:text-gray-400'>
                {t('paymentEnjoy')}
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className='w-full bg-primary-600 text-white py-3 rounded-xl font-semibold hover:bg-primary-700 transition-colors'
              >
                {t('goDashboard')}
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircleIcon className='w-20 h-20 text-red-500 mx-auto mb-6' />
              <h2 className='text-2xl font-bold text-gray-900 mb-3 dark:text-white'>{t('paymentFailed')}</h2>
              <p className='text-gray-600 mb-8 dark:text-gray-300'>
                {t('paymentFailedHint')}
              </p>
              <div className='flex gap-3'>
                <button
                  onClick={() => navigate('/pricing')}
                  className='flex-1 border border-gray-300 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors'
                >
                  {t('backToPricing')}
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className='flex-1 bg-primary-600 text-white py-3 rounded-xl font-semibold hover:bg-primary-700 transition-colors'
                >
                  {t('dashboard')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

export default PaymentSuccess;
